import React, { useState, useEffect, useMemo } from 'react';
import {
  collection, getDocs, addDoc, updateDoc, deleteDoc,
  doc, orderBy, query, serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import { buildCampaignLink, toSlug } from '../utils/registrationTracker';
import {
  Plus, Copy, Check, Edit2, Trash2, Link2,
  ToggleLeft, ToggleRight, X, Building2,
} from 'lucide-react';
import { toast } from 'react-toastify';

const SOURCE_OPTIONS = [
  { value: 'instagram', label: 'Instagram' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'tiktok', label: 'TikTok' },
  { value: 'twitter', label: 'Twitter / X' },
  { value: 'google', label: 'Google' },
  { value: 'google_ads', label: 'Google Ads' },
  { value: 'youtube', label: 'YouTube' },
  { value: 'wp', label: 'WordPress' },
  { value: 'email', label: 'E-posta' },
  { value: 'referral', label: 'Referans' },
  { value: 'other', label: 'Diğer' },
];

const MEDIUM_OPTIONS = [
  { value: 'bio', label: 'Bio Linki' },
  { value: 'story', label: 'Story' },
  { value: 'ad', label: 'Reklam' },
  { value: 'cpc', label: 'CPC (Ücretli)' },
  { value: 'email', label: 'E-posta' },
  { value: 'dm', label: 'DM / Mesaj' },
  { value: 'link', label: 'Link' },
  { value: 'organic', label: 'Organik' },
  { value: 'other', label: 'Diğer' },
];

const EMPTY_FORM = {
  name: '',
  utmSource: '',
  utmMedium: '',
  utmCampaign: '',
  ref: '',
  utmContent: '',
};

const fmtDate = (ts) => {
  if (!ts) return '—';
  const d = ts?.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('tr-TR', { day: '2-digit', month: 'short', year: 'numeric' });
};

export default function AdminCampaigns() {
  const [campaigns, setCampaigns] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [campSnap, bizSnap] = await Promise.all([
        getDocs(query(collection(db, 'campaigns'), orderBy('createdAt', 'desc'))),
        getDocs(collection(db, 'businesses')),
      ]);
      setCampaigns(campSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setBusinesses(bizSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch {
      toast.error('Kampanyalar yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const businessCountByCampaign = useMemo(() => {
    const map = {};
    businesses.forEach((b) => {
      if (b.campaignId) map[b.campaignId] = (map[b.campaignId] || 0) + 1;
    });
    return map;
  }, [businesses]);

  const generatedLink = useMemo(() => buildCampaignLink(form), [form]);

  const handleNameChange = (e) => {
    const name = e.target.value;
    const slug = toSlug(name);
    setForm((prev) => ({ ...prev, name, ref: slug, utmCampaign: slug }));
  };

  const handleFormChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const openEdit = (camp) => {
    setEditingId(camp.id);
    setForm({
      name: camp.name || '',
      utmSource: camp.utmSource || '',
      utmMedium: camp.utmMedium || '',
      utmCampaign: camp.utmCampaign || '',
      ref: camp.ref || '',
      utmContent: camp.utmContent || '',
    });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Kampanya adı gerekli'); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        ref: form.ref.trim(),
        utmSource: form.utmSource,
        utmMedium: form.utmMedium,
        utmCampaign: form.utmCampaign.trim(),
        utmContent: form.utmContent.trim(),
      };
      if (editingId) {
        await updateDoc(doc(db, 'campaigns', editingId), payload);
        setCampaigns((prev) => prev.map((c) => c.id === editingId ? { ...c, ...payload } : c));
        toast.success('Kampanya güncellendi');
      } else {
        const ref = await addDoc(collection(db, 'campaigns'), {
          ...payload,
          isActive: true,
          createdAt: serverTimestamp(),
        });
        setCampaigns((prev) => [{ id: ref.id, ...payload, isActive: true, createdAt: new Date() }, ...prev]);
        toast.success('Kampanya oluşturuldu');
      }
      setShowForm(false);
      setEditingId(null);
      setForm(EMPTY_FORM);
    } catch {
      toast.error('Kampanya kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (camp) => {
    try {
      await updateDoc(doc(db, 'campaigns', camp.id), { isActive: !camp.isActive });
      setCampaigns((prev) => prev.map((c) => c.id === camp.id ? { ...c, isActive: !c.isActive } : c));
    } catch {
      toast.error('Güncelleme başarısız');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu kampanyayı silmek istediğinizden emin misiniz?')) return;
    setDeletingId(id);
    try {
      await deleteDoc(doc(db, 'campaigns', id));
      setCampaigns((prev) => prev.filter((c) => c.id !== id));
      toast.success('Kampanya silindi');
    } catch {
      toast.error('Silinemedi');
    } finally {
      setDeletingId(null);
    }
  };

  const copyLink = (camp) => {
    navigator.clipboard.writeText(buildCampaignLink(camp));
    setCopiedId(camp.id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  return (
    <div className="space-y-6">
      {/* Başlık */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Link2 className="h-6 w-6 text-blue-500" />
            Kampanyalar
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">Kayıt linkleri oluştur, kopyala ve takip et</p>
        </div>
        {!showForm && (
          <button
            onClick={openCreate}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="h-4 w-4" />
            Yeni Kampanya
          </button>
        )}
      </div>

      {/* Form */}
      {showForm && (
        <div className="bg-white rounded-xl border border-blue-200 p-6 shadow-sm">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-base font-semibold text-gray-800">
              {editingId ? 'Kampanyayı Düzenle' : 'Yeni Kampanya Oluştur'}
            </h2>
            <button
              onClick={() => { setShowForm(false); setEditingId(null); }}
              className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <form onSubmit={handleSave} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Kampanya Adı */}
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Kampanya Adı <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={handleNameChange}
                  placeholder="ör. Instagram Mayıs 2026"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
                <p className="text-[10px] text-gray-400 mt-1">Ad girilince ref kodu ve kampanya etiketi otomatik oluşturulur.</p>
              </div>

              {/* Kaynak */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Kaynak (utm_source)</label>
                <select
                  name="utmSource"
                  value={form.utmSource}
                  onChange={handleFormChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                >
                  <option value="">Seç…</option>
                  {SOURCE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>

              {/* Ortam */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Ortam (utm_medium)</label>
                <select
                  name="utmMedium"
                  value={form.utmMedium}
                  onChange={handleFormChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                >
                  <option value="">Seç…</option>
                  {MEDIUM_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>

              {/* Ref kodu */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Kısa Ref Kodu</label>
                <input
                  type="text"
                  name="ref"
                  value={form.ref}
                  onChange={handleFormChange}
                  placeholder="ör. instagram-mayis26"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Kampanya etiketi */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Kampanya Etiketi (utm_campaign)</label>
                <input
                  type="text"
                  name="utmCampaign"
                  value={form.utmCampaign}
                  onChange={handleFormChange}
                  placeholder="ör. mayis2026"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* İçerik */}
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  İçerik (utm_content) — <span className="font-normal text-gray-400">opsiyonel, farklı reklam varyantları için</span>
                </label>
                <input
                  type="text"
                  name="utmContent"
                  value={form.utmContent}
                  onChange={handleFormChange}
                  placeholder="ör. banner-mavi"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Link Önizleme */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="text-xs font-medium text-blue-600 mb-2">Oluşturulan Kayıt Linki:</div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-mono text-gray-700 break-all flex-1 leading-relaxed">{generatedLink}</span>
                <button
                  type="button"
                  onClick={() => { navigator.clipboard.writeText(generatedLink); toast.success('Kopyalandı!'); }}
                  className="p-2 text-blue-400 hover:text-blue-600 hover:bg-blue-100 rounded-lg transition-colors flex-shrink-0"
                  title="Kopyala"
                >
                  <Copy className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="flex gap-2 justify-end pt-1">
              <button
                type="button"
                onClick={() => { setShowForm(false); setEditingId(null); setForm(EMPTY_FORM); }}
                className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                İptal
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-5 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-60"
              >
                {saving ? 'Kaydediliyor…' : editingId ? 'Güncelle' : 'Oluştur'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Kampanya Listesi */}
      {loading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
        </div>
      ) : campaigns.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 py-20 text-center">
          <Link2 className="h-10 w-10 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-400 mb-1">Henüz kampanya yok.</p>
          <button onClick={openCreate} className="text-sm text-blue-600 hover:underline">
            İlk kampanyayı oluştur →
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr className="text-xs text-gray-500 uppercase tracking-wide">
                  <th className="px-5 py-3 text-left">Kampanya</th>
                  <th className="px-4 py-3 text-left">Kaynak / Ortam</th>
                  <th className="px-4 py-3 text-left">Kayıt Linki</th>
                  <th className="px-4 py-3 text-center">İşletme</th>
                  <th className="px-4 py-3 text-left">Tarih</th>
                  <th className="px-4 py-3 text-center">Aktif</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {campaigns.map((camp) => {
                  const link = buildCampaignLink(camp);
                  const count = businessCountByCampaign[camp.id] || 0;
                  return (
                    <tr key={camp.id} className={`hover:bg-gray-50 transition-colors ${!camp.isActive ? 'opacity-50' : ''}`}>
                      <td className="px-5 py-3">
                        <div className="font-medium text-gray-800">{camp.name}</div>
                        {camp.ref && (
                          <div className="text-[10px] text-gray-400 font-mono mt-0.5">ref={camp.ref}</div>
                        )}
                      </td>

                      <td className="px-4 py-3">
                        <div className="text-xs font-medium text-gray-700">{camp.utmSource || '—'}</div>
                        {camp.utmMedium && (
                          <div className="text-[10px] text-gray-400">{camp.utmMedium}</div>
                        )}
                      </td>

                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5 max-w-[220px]">
                          <span
                            className="text-[10px] font-mono text-blue-600 truncate"
                            title={link}
                          >
                            {link.replace('https://', '')}
                          </span>
                          <button
                            onClick={() => copyLink(camp)}
                            className="flex-shrink-0 p-1 text-gray-400 hover:text-blue-600 rounded transition-colors"
                            title="Linki kopyala"
                          >
                            {copiedId === camp.id
                              ? <Check className="h-3.5 w-3.5 text-green-500" />
                              : <Copy className="h-3.5 w-3.5" />
                            }
                          </button>
                        </div>
                        {camp.utmCampaign && (
                          <div className="text-[10px] text-gray-400 font-mono mt-0.5">
                            utm_campaign={camp.utmCampaign}
                          </div>
                        )}
                      </td>

                      <td className="px-4 py-3 text-center">
                        {count > 0 ? (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-700 bg-green-50 px-2 py-0.5 rounded-full border border-green-100">
                            <Building2 className="h-3 w-3" />
                            {count}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-300">—</span>
                        )}
                      </td>

                      <td className="px-4 py-3 text-xs text-gray-400 whitespace-nowrap">
                        {fmtDate(camp.createdAt)}
                      </td>

                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => toggleActive(camp)}
                          className="text-gray-400 hover:text-blue-600 transition-colors"
                          title={camp.isActive ? 'Pasife al' : 'Aktif et'}
                        >
                          {camp.isActive
                            ? <ToggleRight className="h-5 w-5 text-blue-500" />
                            : <ToggleLeft className="h-5 w-5" />
                          }
                        </button>
                      </td>

                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1 justify-end">
                          <button
                            onClick={() => openEdit(camp)}
                            className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Düzenle"
                          >
                            <Edit2 className="h-3.5 w-3.5" />
                          </button>
                          <button
                            onClick={() => handleDelete(camp.id)}
                            disabled={deletingId === camp.id}
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-40"
                            title="Sil"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
